/**
 * phraseMatch — find a user-selected phrase in rendered text, ignoring case and
 * whitespace, and mark it.
 *
 * Whitespace is ignored because the same label text is laid out differently on
 * screen (PDF text-layer spans, <br>s, paragraphs) than in the export (page text
 * joined with spaces), and a browser selection's toString() reflects the screen
 * layout. The Compare page uses this both to paint manual highlights on screen
 * and to mark them in the exported HTML, so the two always agree.
 */

/** A slice [start, end) of one text node that is part of a match. */
export interface TextSegment {
  node: Text;
  start: number;
  end: number;
}

// Subtrees never searched: non-rendered text, and anything the caller opts out.
const ALWAYS_SKIPPED = 'script, style';

/** Lower-cases one UTF-16 unit, keeping it one unit long so indexes stay aligned. */
function fold(ch: string): string {
  const lower = ch.toLowerCase();
  return lower.length === 1 ? lower : ch;
}

/** The comparison form of a phrase: case-folded, all whitespace removed. */
export function normalizePhrase(phrase: string): string {
  return Array.from(phrase.replace(/\s+/g, ''), fold).join('');
}

/**
 * Every occurrence of `phrase` in the text under `root`, as the text-node slices
 * each occurrence covers (one occurrence can span several nodes). Subtrees
 * matching `skipSelector` are ignored.
 */
export function findPhraseSegments(root: Node, phrase: string, skipSelector?: string): TextSegment[][] {
  const needle = normalizePhrase(phrase);
  if (!needle) return [];
  const skip = skipSelector ? `${ALWAYS_SKIPPED}, ${skipSelector}` : ALWAYS_SKIPPED;
  const doc = root.ownerDocument ?? (root as Document);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (n) =>
      n.nodeType === Node.ELEMENT_NODE && (n as Element).matches(skip)
        ? NodeFilter.FILTER_REJECT // skips the whole subtree
        : NodeFilter.FILTER_ACCEPT,
  });

  // The whitespace-free, case-folded text, with each character's origin.
  const chars: string[] = [];
  const originNode: Text[] = [];
  const originOffset: number[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType !== Node.TEXT_NODE) continue;
    const text = n as Text;
    const data = text.data;
    for (let i = 0; i < data.length; i++) {
      if (/\s/.test(data[i]!)) continue;
      chars.push(fold(data[i]!));
      originNode.push(text);
      originOffset.push(i);
    }
  }

  const haystack = chars.join('');
  const matches: TextSegment[][] = [];
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, at + needle.length)) {
    const segments: TextSegment[] = [];
    for (let i = at; i < at + needle.length; i++) {
      const last = segments[segments.length - 1];
      if (last && last.node === originNode[i]) last.end = originOffset[i]! + 1;
      else segments.push({ node: originNode[i]!, start: originOffset[i]!, end: originOffset[i]! + 1 });
    }
    matches.push(segments);
  }
  return matches;
}

/**
 * Wrap every occurrence of `phrases` in `html` in
 * `<mark style="background:COLOR" ATTR>`. Returns null when nothing matched, so
 * callers can leave untouched content exactly as it was.
 */
export function markPhrasesInHtml(html: string, phrases: string[], color: string, attr: string): string | null {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const ranges = new Map<Text, [number, number][]>();
  for (const phrase of phrases) {
    for (const seg of findPhraseSegments(body, phrase).flat()) {
      const list = ranges.get(seg.node) ?? [];
      list.push([seg.start, seg.end]);
      ranges.set(seg.node, list);
    }
  }
  if (!ranges.size) return null;

  for (const [node, list] of ranges) {
    // Wrap right-to-left so the offsets of the slices still to wrap stay valid.
    for (const [start, end] of mergeRanges(list).reverse()) {
      const slice = node.splitText(start);
      slice.splitText(end - start);
      const mark = body.ownerDocument.createElement('mark');
      mark.setAttribute('style', `background:${color}`);
      mark.setAttribute(attr, '');
      slice.replaceWith(mark);
      mark.append(slice);
    }
  }
  return body.innerHTML;
}

/** Sorted, non-overlapping union of [start, end) ranges. */
function mergeRanges(list: [number, number][]): [number, number][] {
  const sorted = [...list].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  for (const [start, end] of sorted) {
    const last = out[out.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else out.push([start, end]);
  }
  return out;
}
